import ButtonWithAnalytics from "@/features/Common/components/ButtonWithAnalytics";
import IconLoader from "@/features/Common/components/IconLoader";
import { VoucherPaymentReceipt } from "@/services/core-api-adapter";
import { panelStyle } from "@/theme";
import {
  convertPxToRem,
  formatAmount,
  formatDateToReadableString,
  getShortDayFromDate,
} from "@/utils";
import { Box, Chip, Divider, Stack, Typography } from "@mui/material";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

interface VoucherOrderSummaryProps {
  voucherPaymentReceipt: VoucherPaymentReceipt;
  onViewReceiptClick: () => void;
}

export default function VoucherOrderSummary({
  voucherPaymentReceipt,
  onViewReceiptClick,
}: VoucherOrderSummaryProps) {
  const { t } = useTranslation();
  const [voucherType, setVoucherType] = useState<string>("");

  useEffect(() => {
    if (voucherPaymentReceipt?.voucherTypeName === "Doctor") {
      setVoucherType("doctorVoucher");
    } else if (voucherPaymentReceipt?.voucherTypeName === "Nurse") {
      setVoucherType("nurseVoucher");
    } else {
      setVoucherType("bloodTestVoucher");
    }
  }, [voucherPaymentReceipt]);

  return (
    <Stack>
      <Stack justifyContent="center" alignItems="center" pt={5}>
        <Typography variant="h1">{t("OrderSummary.title")}</Typography>
        <Typography variant="body1" pt={2}>
          {t("OrderSummary.subtitle")}
        </Typography>
        <Typography variant="body1" pb={2}>
          {voucherPaymentReceipt?.voucherCode}
        </Typography>

        <Chip
          icon={<IconLoader icon="CloudStatusIcon" />}
          label={t("OrderSummary.pillText")}
          sx={{ borderRadius: 2 }}
        />

        <Stack
          direction="row"
          gap={1}
          pt={2}
          alignItems="center"
          justifyContent="center"
        >
          <Chip
            icon={<IconLoader color="success" icon="CheckInCircleFilledIcon" />}
            label={t("OrderSummary.chipText")}
            sx={{
              borderRadius: 2,
              color: "green",
              borderWidth: 1,
              borderColor: "black",
            }}
          />

          <Typography variant="body1">
            {voucherPaymentReceipt?.paymentUtcDateTime
              ? getShortDayFromDate(
                  new Date(voucherPaymentReceipt.paymentUtcDateTime)
                )
              : ""}
            ,{" "}
            {voucherPaymentReceipt?.paymentUtcDateTime
              ? formatDateToReadableString(
                  new Date(voucherPaymentReceipt.paymentUtcDateTime)
                )
              : ""}
          </Typography>
        </Stack>

        <Stack
          spacing={2}
          padding={2}
          pt={5}
          width="100%"
          sx={{ ...panelStyle, marginTop: 2 }}
        >
          <Typography variant="h3" fontWeight={600}>
            {t(`OrderSummary.PanelOne.${voucherType}.title` as any)}
          </Typography>
          <Divider sx={{ width: "100%" }} />
          <Typography>{t("OrderSummary.PanelOne.description")}</Typography>
          <ButtonWithAnalytics
            page="VoucherOrderSummary"
            text={t("OrderSummary.PanelOne.shareButton")}
            intent="navigational"
            onClick={() => {}}
            size="small"
          >
            {t("OrderSummary.PanelOne.shareButton")}
          </ButtonWithAnalytics>
          <ButtonWithAnalytics
            page="VoucherOrderSummary"
            text={t("OrderSummary.PanelOne.saveButton")}
            intent="navigational"
            variant="outlined"
            onClick={() => {}}
            size="small"
          >
            {t("OrderSummary.PanelOne.saveButton")}
          </ButtonWithAnalytics>
        </Stack>

        <Stack
          spacing={2}
          padding={2}
          pt={5}
          width="100%"
          sx={{ ...panelStyle, marginTop: 2 }}
        >
          <Typography variant="h3" fontWeight={600}>
            {t("OrderSummary.PanelTwo.title")}
          </Typography>
          <Divider />
          <Box
            display="flex"
            flexDirection="row"
            justifyContent="space-between"
          >
            <Stack>
              <Typography variant="h4" fontWeight={400}>
                {t(`OrderSummary.PanelOne.${voucherType}.title` as any)}
              </Typography>
              {voucherPaymentReceipt.isDiscountApplied && (
                <Typography
                  color="neutral.light"
                  variant="body2"
                  sx={{ textDecoration: "line-through" }}
                >
                  {formatAmount(voucherPaymentReceipt?.originalAmount, "ZAR")}
                </Typography>
              )}
            </Stack>
            <Typography variant="h4" color="primary">
              {formatAmount(voucherPaymentReceipt?.paymentAmount, "ZAR")}
            </Typography>
          </Box>
          <Divider sx={{ width: "100%" }} />
          <Stack direction={"row"} justifyContent="space-between">
            <Typography variant="h4">
              {t("OrderSummary.PanelTwo.totalText")}
            </Typography>
            <Typography
              variant="h4"
              sx={{ fontSize: convertPxToRem(26) }}
              color="primary"
            >
              {formatAmount(voucherPaymentReceipt?.paymentAmount, "ZAR")}
            </Typography>
          </Stack>

          <Divider sx={{ width: "100%" }} />
          <ButtonWithAnalytics
            page="VoucherOrderSummary"
            text={t("OrderSummary.bottomButton")}
            intent="navigational"
            onClick={onViewReceiptClick}
            size="small"
          >
            {t("OrderSummary.bottomButton")}
          </ButtonWithAnalytics>
        </Stack>
      </Stack>
    </Stack>
  );
}
